import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Star, Users, Trophy, AlertTriangle, CheckCircle, Clock,
  ArrowUp, ArrowDown, Minus, ChevronRight, MessageSquare, User, Sparkles, Target
} from 'lucide-react';

// Approved Summary Display - shown to employee
const ApprovedSummarySection = ({ summary }) => {
  if (!summary) return null;
  
  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Feedback și Sugestii
        </CardTitle>
        <CardDescription>
          Rezumat aprobat de management
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {summary.summary_text && (
          <div className="p-3 bg-white rounded-lg border">
            <p className="text-sm">{summary.summary_text}</p>
          </div>
        )}
        
        {summary.suggestions?.length > 0 && (
          <div>
            <p className="text-sm font-medium flex items-center gap-1 mb-2">
              <Target className="w-4 h-4 text-orange-500" />
              Sugestii de îmbunătățire:
            </p>
            <div className="space-y-2">
              {summary.suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-white rounded border-l-2 border-orange-400">
                  <span className="text-orange-500 font-medium text-sm mt-0.5">{i + 1}.</span>
                  <p className="text-sm">{s}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Star Rating Component
const StarRating = ({ value, onChange, readonly = false, size = 'md' }) => {
  const [hovered, setHovered] = useState(0);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };
  
  const handleClick = (rating) => {
    if (!readonly && onChange) {
      onChange(rating);
    }
  };
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => handleClick(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hovered || value)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-gray-200 text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Score Badge with color coding
const ScoreBadge = ({ score, size = 'md' }) => {
  if (!score && score !== 0) return <span className="text-muted-foreground">-</span>;
  
  const getColor = (s) => {
    if (s >= 4) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (s >= 3) return 'bg-amber-100 text-amber-700 border-amber-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-lg px-3 py-1.5 font-bold'
  };
  
  return (
    <span className={`rounded-md border ${getColor(score)} ${sizeClasses[size]}`}>
      {score.toFixed(1)}
    </span>
  );
};

// Trend Arrow
const TrendArrow = ({ current, previous }) => {
  if (!previous) return <Minus className="w-4 h-4 text-gray-400" />;
  
  const diff = current - previous;
  if (diff > 0.1) return <ArrowUp className="w-4 h-4 text-emerald-500" />;
  if (diff < -0.1) return <ArrowDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
};

// Evaluation Form Dialog
const EvaluationDialog = ({ colleague, isOpen, onClose, onSubmit }) => {
  const [scores, setScores] = useState({
    collaboration: 0,
    quality: 0,
    deadlines: 0,
    communication: 0
  });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const criteria = [
    { key: 'collaboration', label: 'Colaborare & Ajutor reciproc', icon: '🤝' },
    { key: 'quality', label: 'Calitatea Muncii', icon: '⭐' },
    { key: 'deadlines', label: 'Respectare Deadline-uri', icon: '⏱' },
    { key: 'communication', label: 'Comunicare & Atitudine', icon: '💬' }
  ];
  
  const allScoresSet = Object.values(scores).every(s => s > 0);
  
  const handleSubmit = async () => {
    if (!allScoresSet) return;
    
    setSubmitting(true);
    try {
      await onSubmit(colleague.id, scores, comment);
      onClose();
      setScores({ collaboration: 0, quality: 0, deadlines: 0, communication: 0 });
      setComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la trimiterea evaluării');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!colleague) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-500" />
            Evaluează: {colleague.full_name}
          </DialogTitle>
          <DialogDescription>
            Evaluează performanța colegului tău pentru această săptămână
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {criteria.map(({ key, label, icon }) => (
            <div key={key} className="space-y-2">
              <Label className="flex items-center gap-2 text-base">
                <span>{icon}</span>
                {label}
              </Label>
              <div className="flex items-center gap-4">
                <StarRating
                  value={scores[key]}
                  onChange={(val) => setScores({ ...scores, [key]: val })}
                  size="lg"
                />
                {scores[key] > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {scores[key]} / 5
                  </span>
                )}
              </div>
            </div>
          ))}
          
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Comentariu (opțional)
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Adaugă un comentariu constructiv..."
              rows={3}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Anulează
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!allScoresSet || submitting}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {submitting ? 'Se trimite...' : 'Trimite Evaluarea'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Colleague Card for evaluation
const ColleagueCard = ({ colleague, onEvaluate }) => {
  return (
    <div className={`p-4 border rounded-lg flex items-center justify-between ${
      colleague.is_reviewed ? 'bg-emerald-50 border-emerald-200' : 'bg-white hover:border-orange-300'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
          colleague.is_reviewed ? 'bg-emerald-500' : 'bg-slate-400'
        }`}>
          {colleague.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </div>
        <div>
          <p className="font-medium">{colleague.full_name}</p>
          <p className="text-sm text-muted-foreground">{colleague.email}</p>
        </div>
      </div>
      
      {colleague.is_reviewed ? (
        <Badge className="bg-emerald-100 text-emerald-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Evaluat
        </Badge>
      ) : (
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onEvaluate(colleague)}
          className="border-orange-300 text-orange-600 hover:bg-orange-50"
        >
          Evaluează
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
};

// Main Employee Peer Review View
export const EmployeePeerReview = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedColleague, setSelectedColleague] = useState(null);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [myReviews, setMyReviews] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [colleaguesRes, myReviewsRes] = await Promise.all([
        api.get('/peer-review/colleagues'),
        api.get('/peer-review/my-reviews')
      ]);
      setData(colleaguesRes.data);
      setMyReviews(myReviewsRes.data);
    } catch (err) {
      console.error('Error loading peer review data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitReview = async (targetId, scores, comment) => {
    await api.post('/peer-review/submit', {
      target_id: targetId,
      scores,
      comment
    });
    toast.success('Evaluarea a fost trimisă!');
    loadData();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  if (!data?.active_round && !myReviews?.can_see_reviews) {
    return (
      <div className="space-y-6">
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">Nu există rundă activă</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Așteaptă ca administratorul să pornească o nouă rundă de evaluare.
            </p>
          </CardContent>
        </Card>
        
        {/* Show message about results */}
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="py-6 text-center">
            <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-muted-foreground">
              {myReviews?.message || 'Rezultatele vor fi disponibile după ce administratorul închide runda de evaluare.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Show results from closed round (no active round but has results)
  if (!data?.active_round && myReviews?.can_see_reviews) {
    return (
      <div className="space-y-6">
        <Card className="border-slate-200">
          <CardContent className="py-8 text-center">
            <Clock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <h3 className="text-base font-medium text-slate-600">Nu există rundă activă</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Așteaptă ca administratorul să pornească o nouă rundă de evaluare.
            </p>
          </CardContent>
        </Card>
        
        {/* Anonymous results from last closed round */}
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-5 h-5 text-blue-500" />
              Rezultatele tale ({myReviews.round?.week_label})
            </CardTitle>
            <CardDescription>
              {myReviews.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {myReviews.scores?.average_scores ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Colaborare</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.collaboration)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.collaboration} size="sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Calitate</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.quality)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.quality} size="sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Deadline-uri</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.deadlines)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.deadlines} size="sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Comunicare</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.communication)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.communication} size="sm" />
                    </div>
                  </div>
                </div>
                
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-muted-foreground">Scor General</p>
                  <ScoreBadge score={myReviews.scores.overall_average} size="lg" />
                  <p className="text-xs text-muted-foreground mt-2">
                    Bazat pe {myReviews.scores.total_reviews} evaluări anonime
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">
                Nu ai primit evaluări.
              </p>
            )}
          </CardContent>
        </Card>
        
        {/* Approved suggestions from admin */}
        <ApprovedSummarySection summary={myReviews?.approved_summary} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Evaluare Colaborare
          </h2>
          <p className="text-sm text-muted-foreground">
            Săptămâna: {data.round?.week_label}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Progres</p>
          <p className="font-bold text-lg">
            {data.completed_count} / {data.colleagues?.length || 0} evaluate
          </p>
        </div>
      </div>
      
      {/* Progress notification */}
      {data.pending_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span className="text-amber-700">
            Ai <strong>{data.pending_count}</strong> colegi de evaluat această săptămână.
          </span>
        </div>
      )}
      
      {/* Colleagues to evaluate */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Colegii tăi</CardTitle>
          <CardDescription>
            Evaluează fiecare coleg. Rezultatele sunt anonime și vizibile după închiderea rundei.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.colleagues?.map((colleague) => (
            <ColleagueCard
              key={colleague.id}
              colleague={colleague}
              onEvaluate={(c) => {
                setSelectedColleague(c);
                setShowEvalDialog(true);
              }}
            />
          ))}
        </CardContent>
      </Card>
      
      {/* My received reviews - anonymous averages only, visible after round is closed */}
      {myReviews?.can_see_reviews ? (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-5 h-5 text-blue-500" />
              Rezultatele tale ({myReviews.round?.week_label})
            </CardTitle>
            <CardDescription>
              {myReviews.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {myReviews.scores?.average_scores ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Colaborare</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.collaboration)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.collaboration} size="sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Calitate</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.quality)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.quality} size="sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Deadline-uri</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.deadlines)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.deadlines} size="sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Comunicare</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <StarRating value={Math.round(myReviews.scores.average_scores.communication)} readonly size="sm" />
                      <ScoreBadge score={myReviews.scores.average_scores.communication} size="sm" />
                    </div>
                  </div>
                </div>
                
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-muted-foreground">Scor General</p>
                  <ScoreBadge score={myReviews.scores.overall_average} size="lg" />
                  <p className="text-xs text-muted-foreground mt-2">
                    Bazat pe {myReviews.scores.total_reviews} evaluări anonime
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">
                Nu ai primit evaluări.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="py-6 text-center">
            <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-muted-foreground">
              {myReviews?.message || 'Rezultatele vor fi disponibile după ce administratorul închide runda de evaluare.'}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Approved suggestions from admin (shown in active round view too) */}
      <ApprovedSummarySection summary={myReviews?.approved_summary} />
      
      {/* Evaluation Dialog */}
      <EvaluationDialog
        colleague={selectedColleague}
        isOpen={showEvalDialog}
        onClose={() => {
          setShowEvalDialog(false);
          setSelectedColleague(null);
        }}
        onSubmit={handleSubmitReview}
      />
    </div>
  );
};

export default EmployeePeerReview;
